import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react'
import { useAuthStore } from '@indipia/shared'
import type { NavFn, DirtyHandle } from '../pages/SchedulerPage'
import type { SiteInfo } from './CalendarMultiView'
import { useCommonCodes } from '../context/CommonCodesContext'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { type MaterialOrderChange } from './MaterialOrderPopup'
import { useShipLedger } from '../hooks/useShipLedger'
import { useShipVendorPhones } from '../hooks/useShipVendorPhones'
import { shipToggleSidePartner, type ShipLedgerRow } from '../constants/ship.logic'
import { OnboardingTour, type OnboardingTourHandle, type TourStep } from '../components/OnboardingTour'
import { useSignalLayer, type SignalLayerDefaults } from '../hooks/useSignalLayer'
import { useScheduleOnlyCodes } from '../hooks/useScheduleOnlyCodes'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import { SignalPopover } from '../components/SignalPopover'
import { SignalTodoPanel } from '../components/SignalTodoPanel'
import {
  Page,                             // §15.41 — layout="fixed"(스크롤 소유권, D-07 §21.4)
  EmptyState, ErrorState, Skeleton, // §15.37 · §15.30 — 상태 UX(D-07 §17.1)
  SignalLegend,                     // §15.53 — 신호등 범례 줄(승인된 신규 N7)
} from '@indipia/ui-core'
import { getPredecessorCandidates, holidayGateError, movedRange, resizedRange, fmtMD, workdaysOrNull } from './scheduleUtils'
import type { GanttDeepLink, GanttTask } from '../types/gantt.types'
import { SIGNAL_LEGEND_ITEMS, isSignalUnavailableSchedule } from '../constants/signalView'
import { addDays, daysBetween, buildMilestoneDayMap, buildDisplayRows, buildArrows, buildConnectorPoints, buildFloatBars } from '../constants/gantt.logic'
import { printGantt } from '../components/gantt/ganttPrint'
import { GanttEmptySite } from '../components/gantt/GanttEmptySite'
import { GanttInfoBar } from '../components/gantt/GanttInfoBar'
import { GanttOptionsBar } from '../components/gantt/GanttOptionsBar'
import { GanttNoticeStrips } from '../components/gantt/GanttNoticeStrips'
import { GanttHeaderRows } from '../components/gantt/GanttHeaderRows'
import { GanttOverlay } from '../components/gantt/GanttOverlay'
import { GanttRemarkBar } from '../components/gantt/GanttRemarkBar'
import { GanttMemoTooltip } from '../components/gantt/GanttMemoTooltip'
import { GanttContextMenu } from '../components/gantt/GanttContextMenu'
import { GanttConfirmModals } from '../components/gantt/GanttConfirmModals'
import { useGanttMilestones } from '../hooks/useGanttMilestones'
import { usePeriodSyncConfirm } from '../hooks/usePeriodSyncConfirm'
import { useSiteRefresh } from '../context/SiteRefreshContext'
import { GanttMilestoneModal } from '../components/gantt/GanttMilestoneModal'
import { useGanttPan } from '../hooks/useGanttPan'
import { useGanttSignalBridge } from '../hooks/useGanttSignalBridge'
import { useGanttDeepLink } from '../hooks/useGanttDeepLink'
import { useSignalAction } from '../hooks/useSignalAction'
import { useGanttData } from '../hooks/useGanttData'
import { useGanttLayout } from '../hooks/useGanttLayout'
import { useGanttDateMove } from '../hooks/useGanttDateMove'
import { GanttDateAlerts } from '../components/gantt/GanttDateAlerts'
import { useGanttBarDrag } from '../hooks/useGanttBarDrag'
import { useGanttConnector } from '../hooks/useGanttConnector'
import { useGanttMarkerDrag } from '../hooks/useGanttMarkerDrag'
import { useGanttPanel } from '../hooks/useGanttPanel'
import { useGanttPartnerPatch } from '../hooks/useGanttPartnerPatch'
import { GanttDetailPanelHost } from '../components/gantt/GanttDetailPanelHost'
import { GanttTaskRowLeft } from '../components/gantt/GanttTaskRowLeft'
import { GanttBar } from '../components/gantt/GanttBar'
import { GanttBarLabels } from '../components/gantt/GanttBarLabels'
import { GanttSignalDots } from '../components/gantt/GanttSignalDots'
import { GanttPopupHost, GanttAddTaskHost, GanttChangeNoticeHost } from '../components/gantt/GanttPopupHost'

export type { GanttDeepLink }

type Props = { navigate: NavFn; selectedSite?: SiteInfo; onTaskPanelChange?: (open: boolean) => void; onDirtyChange?: (dirty: DirtyHandle | null) => void; embedded?: boolean; deepLink?: GanttDeepLink | null; onDeepLinkConsumed?: () => void; noSchedules?: boolean; /** 신호 층 초기값 — 나의 현장 [진행현황] 진입만 준다(딥링크·메뉴 진입은 주지 않아 꺼짐 유지) */ signalDefaults?: SignalLayerDefaults }

/* ── Main component ──────────────────────────────────────────── */
export function GanttView({ navigate, selectedSite, onTaskPanelChange, onDirtyChange, embedded, deepLink, onDeepLinkConsumed, noSchedules, signalDefaults }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  // 일정·반장·발주 변경 성공 후 서버 신호를 다시 받는다(['signals'] 쿼리 무효화)
  const invalidateSignals = useInvalidateSignals()
  // 스케줄 전용 행정 공정(세대동의서 청구 등, 공종 마스터 scheduleOnlyYn='Y')은 반장·발주·작업 대상이 아니라 신호등을 그리지 않는다
  const scheduleOnlyCodes = useScheduleOnlyCodes()
  // 서버 신호(공종별 반장·발주·작업 3줄)·오늘 할일 + [신호등 켜기]·[빨강·노랑만] 토글·사유 팝오버·할일 패널 상태
  // 현장 종료(거래완료)로 완료된 스케줄은 신호 조회 대상이 아니라 신호등을 켤 수 없다
  const signal = useSignalLayer(selectedSite?.siteId, signalDefaults, scheduleOnlyCodes,
    isSignalUnavailableSchedule(selectedSite?.siteDoneYn))
  const sigOn = signal.signalOn
  // 호스트(shell-web)가 주입한 이동(나의 할일·작업보고확인) — 없으면 해당 링크·버튼을 숨긴다
  const schedulerHost = useSchedulerHost()
  const milestoneTypeOptions = codes?.milestoneTypes ?? []

  const {
    tasks, deps, tasksRef, depsRef, scheduleEndRef, setTasks, setGanttBounds, pendingChangesRef,
    originalTasksRef, setPendingChanges, holidaysRef, ganttBoundsRef, setDeps, milestonesRef, milestones,
    ganttBounds, scheduleRemark, holidays, pendingChanges, setRefreshKey, remarkDraft, setScheduleRemark,
    setMilestones, orderMetaRef, loadedScheduleId, loading, loadError, partners, setRemarkDraft, ordersStatus, retryOrders,
    fetchPartners, insertAfterIdRef, prevTaskIdsRef, holidayState, holidayGateRef, movableRangeRef,
  } = useGanttData({
    selectedSite, user,
  })
  // 보기 옵션 기본값(F-01) — 선후관계·시공파트너·대기일·발주·입고 ON, 자재파트너·바 숨기기·신호등 OFF
  const [showRel,        setShowRel]        = useState(true)
  const [showCont,       setShowCont]       = useState(true)
  // 발주·입고 마커는 기본 ON(W5-26). [자재파트너]는 [시공파트너]와 배타(W5-25, 기본 OFF).
  const [showMarkers,    setShowMarkers]    = useState(true)
  const [showVendor,     setShowVendor]     = useState(false)
  const [showFloat,      setShowFloat]      = useState(true)
  // 바 숨기기 — 켜면 선후관계 화살표·바 자체·대기일 바가 안 보인다(각자 토글과 무관하게 강제 숨김).
  // 반장보기·발주·입고는 바와 무관하게 계속 보여야 하므로 영향받지 않는다.
  const [hideBars,       setHideBars]       = useState(false)
  const [ganttPeriod,    setGanttPeriod]    = useState<'전체' | '1주' | '2주' | '월'>('전체')

  // [시공파트너]·[자재파트너] 배타 토글(W5-25). 칩 onClick 이 updater 를 넘겨도 무시하고 현재 값으로 계산한다.
  const toggleContChip = (_u?: unknown) => {
    const n = shipToggleSidePartner({ foreman: showCont, vendor: showVendor }, 'foreman')
    setShowCont(n.foreman); setShowVendor(n.vendor)
  }
  const toggleVendorChip = (_u?: unknown) => {
    const n = shipToggleSidePartner({ foreman: showCont, vendor: showVendor }, 'vendor')
    setShowCont(n.foreman); setShowVendor(n.vendor)
  }
  // 간트 자재파트너 라벨·입고 완료 표시용 원장(A1)·거래처 전화 — 켜져 있을 때만 조회
  // 자재파트너가 시공하는 공종이 있으면 바 라벨의 업체명을 위해 토글과 무관하게 조회한다
  const hasConstructByVendor = tasks.some(t => t.constructByVendor)
  const ledgerQ = useShipLedger(selectedSite?.scheduleId, showVendor || showMarkers || hasConstructByVendor)
  const phonesQ = useShipVendorPhones(showVendor)
  // 공종별 활성 원장 행(A1 응답 순서 유지, REPLACED 제외). 조회 전·실패 시 빈 맵(라벨 안 그림)
  const shipRowsByTask = useMemo(() => {
    const list = ledgerQ.data
    const m = new Map<number, ShipLedgerRow[]>()
    if (!list) return m
    for (const r of list) {
      if (r.orderStatusCode === 'REPLACED') continue
      const a = m.get(r.taskId)
      if (a) a.push(r); else m.set(r.taskId, [r])
    }
    return m
  }, [ledgerQ.data])
  const [visibleMemoId,   setVisibleMemoId]   = useState<number | null>(null)
  const [memoTooltipPos,  setMemoTooltipPos]  = useState<{ x: number; y: number } | null>(null)
  // 한 공종 행에 바(구간)가 2개 이상일 때 — 소요일 칸이 "N구간 ▼" 버튼으로 바뀌고, 누르면
  // 그 공종의 대표(요약) 행 바로 아래에 구간(바)마다 독립된 행을 하나씩 펼쳐서 나열한다
  // (체크박스·이름 편집·소요일·드래그 이동/리사이즈 등 단일 바 행과 완전히 동일하게 동작).
  // 대표 행의 버튼은 "N구간 ▲"으로 바뀌어 다시 누르면 접힌다. workTypeCode로 펼침 상태를 식별.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const [showAddPop,       setShowAddPop]       = useState(false)
  const [addPopDefaults,   setAddPopDefaults]   = useState<{ workTypeCode?: string; predecessorId: number; defaultStartDate?: string } | null>(null)
  const [ctxMenu,          setCtxMenu]          = useState<{ x: number; y: number; taskId: number; taskName: string; taskEndDate: Date } | null>(null)
  const [selectedTaskIds,  setSelectedTaskIds]  = useState<Set<number>>(new Set())
  const {
    DWRef, scrollRef, clampedGanttOffsetRef, isPanModeRef, setGanttOffset,
    visibleDayRangeRef, today, ganttStart, totalDays, DW, rowH, barTop, attachScrollRef, ganttContainerW,
    DAYS,
  } = useGanttLayout({
    ganttPeriod, selectedSite, ganttBounds, holidays,
  })
  const {
    panelDirty, setSelectedPanelTaskId, selectPanelTaskGuarded, togglePanelTaskGuarded, closePanelGuarded, setPanelForeman,
    setPanelPartnerId, setPanelNotice, panelNotice, selectedPanelTaskId, handleDeleteSelected, localRowNames,
    setLocalRowNames, handleRowNameBlur, panelWorkTypeName, setPanelWorkTypeName, panelPartnerId,
    panelForeman, panelChangeReason, setPanelChangeReason, panelPredIds, setPanelPredIds, panelMemo,
    setPanelMemo, panelError, panelSaving, commitPanel, handleDeleteTask, leavePrompt, setLeavePrompt,
    immediate,
  } = useGanttPanel({
    tasks, deps, onTaskPanelChange, tasksRef, depsRef, selectedSite, user, setTasks, setRefreshKey,
    invalidateSignals, selectedTaskIds, setSelectedTaskIds, scheduleOnlyCodes, partners,
  })
  const [showSigongPanel,     setShowSigongPanel]     = useState(false)
  const [showStatusPanel,     setShowStatusPanel]     = useState(false)
  const [showMaterialPanel,   setShowMaterialPanel]   = useState(false)
  const [showShipPanel,     setShowShipPanel]     = useState(false)
  // 딥링크로 열린 배송정보현황 팝업이 강조할 공종, 공종 목록 조회가 끝난 일정 id
  const [deepLinkTaskId, setDeepLinkTaskId] = useState<number | null>(null)
  const [openDd,               setOpenDd]               = useState<'dd1' | 'dd2' | 'dd3' | null>(null)
  // 자재파트너배정 팝업이 처음 선택할 공종(딥링크·신호 처리 버튼·상세창 카드), 상세창 자재 카드 펼침 신호
  const [materialFocusTaskId, setMaterialFocusTaskId] = useState<number | null>(null)
  const [cardSup,             setCardSup]             = useState<{ taskId: number; nonce: number } | null>(null)
  // 상세창 시공파트너 카드 진행 요약 펼침 신호(card=pt), 막대 외곽선 강조 대상(card=bar), 공종 행 스크롤 요청(F-22·F-23)
  const [cardPt,              setCardPt]              = useState<{ taskId: number; nonce: number } | null>(null)
  const [cardCr,              setCardCr]              = useState<{ taskId: number; nonce: number } | null>(null)
  const [focusBarId,          setFocusBarId]          = useState<number | null>(null)
  const [focusReq,            setFocusReq]            = useState<{ taskId: number; nonce: number; horizontal: boolean } | null>(null)

  const panelTaskRef       = useRef<GanttTask | null>(null)

  const ctxMenuRef     = useRef<HTMLDivElement>(null)
  const tourRef        = useRef<OnboardingTourHandle>(null)
  const chartOverlayRef  = useRef<SVGSVGElement>(null)

  useEffect(() => {
    const handler = () => setOpenDd(null)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  // ctx 메뉴 바깥 클릭/ESC 닫기
  useEffect(() => {
    if (!ctxMenu) return
    const onDown = (e: MouseEvent) => {
      if (ctxMenuRef.current && !ctxMenuRef.current.contains(e.target as Node)) setCtxMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCtxMenu(null) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [ctxMenu])

  useEffect(() => { setSelectedTaskIds(new Set()) }, [selectedSite?.scheduleId])

  // 공종·마커 변경으로 스케줄 기간이 바뀌면 현장 정보(종료일·D-day)와 간트 데이터를 다시 읽는다
  const refreshSites = useSiteRefresh()
  const periodSync = usePeriodSyncConfirm()
  const handlePeriodSynced = useCallback(() => { refreshSites(); setRefreshKey(k => k + 1) }, [refreshSites, setRefreshKey])

  const {
    dragErrorTimer, setDragError, setPullConfirm, applyCascadePlan, dragError, handleCancel, saving,
    handleSave, handleRowDurationChange, pullConfirm, resolvePullConfirm, changeNotice, setChangeNotice,
    orderNotice, setOrderNotice,
  } = useGanttDateMove({
    tasksRef, depsRef, scheduleEndRef, setTasks, setGanttBounds, pendingChangesRef, originalTasksRef,
    setPendingChanges, holidaysRef, holidayGateRef, movableRangeRef, milestonesRef, ganttBoundsRef, selectedSite,
    pendingChanges, user, invalidateSignals, onDirtyChange, panelDirty, setSelectedPanelTaskId,
    confirmPeriodSync: periodSync.confirm, onPeriodSynced: handlePeriodSynced,
  })

  const {
    setSelectedDepId, connectDrag, selectedDepId, onConnectorMouseDown, onConnectorDrop,
  } = useGanttConnector({
    user, depsRef, setDeps, invalidateSignals, setCtxMenu, chartOverlayRef,
  })
  const {
    barDragState, barResizeState, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset,
    onBarMouseDown, onBarResizeMouseDown,
  } = useGanttBarDrag({
    DWRef, tasksRef, movableRangeRef, holidayGateRef,
    togglePanelTaskGuarded, depsRef, dragErrorTimer, setDragError, setPullConfirm, applyCascadePlan,
    holidaysRef, setCtxMenu, setSelectedDepId,
  })

  const {
    onContainerMouseDown, onContainerMouseMove, stopScrollDrag,
  } = useGanttPan({
    scrollRef, closePanelGuarded, setSelectedDepId, clampedGanttOffsetRef, barDragState, barResizeState,
    isPanModeRef, DWRef, setGanttOffset,
  })

  // ── PDF ──────────────────────────────────────────────────────
  const handlePDF = useCallback(() => {
    printGantt({
      selectedSite, tasks, milestones, ganttBounds, showCont, user, scheduleRemark, holidays,
      durationOf: t => workdaysOrNull(holidayState, holidays, t.startDate, t.endDate),
    })
  }, [selectedSite, tasks, milestones, ganttBounds, showCont, user?.compName, scheduleRemark, holidays, holidayState.status, holidayState.from, holidayState.to])

  const {
    handlePartnerSent, handlePartnerChanged, handleForemanAssigned, handleChangeNoticeSent,
  } = useGanttPartnerPatch({
    setTasks, originalTasksRef, setPanelForeman, setPanelPartnerId, setPanelNotice, panelNotice,
  })

  const {
    setShowMilestonePop, draggingMsId, draggingMsDelta, onMilestoneLabelMouseDown, remarkHidden,
    setRemarkHidden, remarkSaving, handleRemarkSave, showMilestonePop, editingMsId, mileEditForm,
    setMileEditForm, setEditingMsId, setMileFormError, mileEditing, handleMileEditSave, handleMileEditOpen,
    mileDeleting, handleMileDelete, mileForm, setMileForm, mileFormError, isMileInvalid, mileSaving,
    handleMileAdd, markerNotice, setMarkerNotice,
  } = useGanttMilestones({
    selectedSite, user, remarkDraft, setScheduleRemark, milestoneTypeOptions, milestones, setMilestones,
    setGanttBounds, visibleDayRangeRef, DWRef, milestonesRef, onMarkerChanged: refreshSites,
  })

  const {
    draggingMkId, draggingMkDelta, onMkMouseDown,
  } = useGanttMarkerDrag({
    DWRef, selectedSite, tasksRef, setTasks, invalidateSignals,
  })

  // 상세창에서 다른 공종을 고르거나 닫으면 카드 펼침 요청을 버린다(같은 공종을 다시 열 때 저절로 펼쳐지지 않게)
  useEffect(() => {
    if (cardSup && selectedPanelTaskId !== cardSup.taskId) setCardSup(null)
  }, [cardSup, selectedPanelTaskId])
  useEffect(() => {
    if (cardPt && selectedPanelTaskId !== cardPt.taskId) setCardPt(null)
  }, [cardPt, selectedPanelTaskId])
  useEffect(() => {
    if (cardCr && selectedPanelTaskId !== cardCr.taskId) setCardCr(null)
  }, [cardCr, selectedPanelTaskId])

  // 간트 막대 옆 '요청 n' 배지 클릭 — 해당 공정 상세창을 열고 고객 요청 카드로 이동(딥링크 card=cr 소비와 같은 경로)
  const handleOpenCustReq = useCallback((taskId: number) => {
    selectPanelTaskGuarded(taskId)
    setCardCr(prev => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [selectPanelTaskGuarded])

  // ── 자재파트너배정 팝업 변경 반영 — 거래처·날짜가 바뀐 공종 1건만 국소 갱신(전체 새로고침 없음) ──
  const handleMaterialChanged = useCallback((c: MaterialOrderChange) => {
    if (c.vendorId != null) orderMetaRef.current.set(c.taskId, { vendorId: c.vendorId, vendorName: c.vendorName ?? '' })
    else orderMetaRef.current.delete(c.taskId)
    setTasks(prev => prev.map(t => t.taskId === c.taskId ? { ...t, orderDate: c.orderDate, deliveryDate: c.deliveryDate } : t))
  }, [])

  const openMaterialPopup = useCallback((taskId: number | null) => {
    setMaterialFocusTaskId(taskId)
    setShowMaterialPanel(true)
  }, [])

  const { panelForemanSignal, signalTaskCtx } = useGanttSignalBridge({ tasks, orderMetaRef, signal })

  // 신호 · 할일 처리 버튼 — 서버 주소(webPath)가 이 현장의 간트 주소면 주소 딥링크와 같은 처리로 그 자리에서 연다(화면을 떠나지 않음)
  const [localDeepLink, setLocalDeepLink] = useState<GanttDeepLink | null>(null)
  const signalAction = useSignalAction((link) => {
    if (!selectedSite || (link.siteId !== null && link.siteId !== selectedSite.siteId)) return false
    setLocalDeepLink({ siteId: selectedSite.siteId, taskId: link.taskId, card: link.card, pop: link.pop })
    return true
  }, selectedSite?.siteId)

  // 막대 강조(F-23)는 다음 조작까지 유지 — 현장 전환·막대 끌기/리사이즈·상세창 열기/닫기·차트 빈 곳 누르기에서 거둔다.
  // 딥링크 소비 훅보다 먼저 선언해야 같은 커밋에서 소비가 켠 강조를 지우지 않는다.
  useEffect(() => { setFocusBarId(null) }, [selectedSite?.scheduleId])
  useEffect(() => { setFocusBarId(null) }, [draggingTaskId, resizingTaskId, selectedPanelTaskId])

  useGanttDeepLink({
    deepLink: localDeepLink ?? deepLink, selectedSite, loadedScheduleId, tasks,
    onDeepLinkConsumed: localDeepLink ? () => setLocalDeepLink(null) : onDeepLinkConsumed, setShowSigongPanel,
    openMaterialPopup, setDeepLinkTaskId, setShowShipPanel, selectPanelTaskGuarded, setCardSup, setCardPt, setCardCr,
    setFocusBarId, setFocusReq, hideBars, setHideBars, ganttPeriod, setGanttPeriod, ganttStart, visibleDayRangeRef,
    setExpandedRows,
  })

  // 공종 지정 딥링크 — 해당 공종 행을 세로 가운데로 스크롤(F-23). 현장 전환 리셋(F-11)은 현장이 바뀐 커밋에서 이미 끝났고
  // 이 요청은 공종 목록 조회가 끝난 뒤에만 오므로 순서가 겹치지 않는다. 표시 상태(바 숨기기·범위·펼침)가 바뀐 커밋 뒤에 실행된다.
  useEffect(() => {
    if (!focusReq) return
    let raf = 0
    let tries = 0
    const run = () => {
      const box = scrollRef.current
      const rows = box ? Array.from(box.querySelectorAll<HTMLElement>(`[data-gantt-row-tasks~="${focusReq.taskId}"]`)) : []
      // 접힌 구간 대표 행과 펼친 개별 행이 함께 걸리면 개별 행(막대가 있는 행)을 우선한다
      const row = rows.find(r => r.dataset.ganttHeader !== '1') ?? rows[0]
      if (!box || !row) {
        if (tries++ < 20) raf = requestAnimationFrame(run)
        return
      }
      const b = box.getBoundingClientRect()
      const r = row.getBoundingClientRect()
      box.scrollTop += r.top - b.top - (b.height - r.height) / 2
      // 전체 범위에서는 막대가 좌측 고정 열(212px) 뒤나 화면 밖에 있을 수 있어 가로도 맞춘다(card=bar 만 — 상세창이 열리면 폭이 바뀐다)
      const bar = focusReq.horizontal && ganttPeriod === '전체' ? box.querySelector<HTMLElement>(`[data-gantt-bar="${focusReq.taskId}"]`) : null
      if (bar) {
        const br = bar.getBoundingClientRect()
        const left = b.left + 212
        if (br.left < left || br.right > b.right) box.scrollLeft += br.left - left - 24
      }
    }
    raf = requestAnimationFrame(run)
    return () => cancelAnimationFrame(raf)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusReq])

  // ── 메모 툴팁 외부 클릭 닫기 ─────────────────────────────────
  useEffect(() => {
    if (visibleMemoId === null) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-memo-popup]')) { setVisibleMemoId(null); setMemoTooltipPos(null) }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [visibleMemoId])

  // 화면 가이드(온보딩 투어) — "?" 버튼으로만 재생한다(첫 방문 자동 재생 없음, F-05). 현장 미선택 화면에서도 같은 단계를 쓰며 화면에 없는 대상은 건너뛴다(F-07).
  // "시공파트너 배정"/"자재파트너 · 발주·입고일" 스텝은 설정 드롭다운(dd2)이 열려 있어야 대상이 보이므로 그때만 연다.
  const ganttTourSteps: TourStep[] = [
    { selector: null, title: '간트차트에 오신 것을 환영해요', body: '공사 일정을 한눈에 보고 관리하는 화면이에요. 핵심 기능을 짧게 안내해드릴게요.', onEnter: () => setOpenDd(null) },
    { selector: '[data-tour="col-header"]', title: '공종과 소요일', body: '작업 항목(공종)과 예상 소요일이 표시돼요. 소요일 옆 화살표로 바로 늘리거나 줄일 수 있어요.', onEnter: () => setOpenDd(null) },
    { selector: '[data-tour="days-header"]', title: '날짜와 오늘 표시', body: '오늘 날짜는 강조돼서 한눈에 확인할 수 있어요. 토·일·공휴일은 색으로 구분되어 있어요.', onEnter: () => setOpenDd(null) },
    { selector: '[data-tour="milestone-row"]', title: '마일스톤', body: '고객현장확인, 입주일처럼 중요한 일정은 다이아몬드 모양의 마일스톤으로 표시돼요.', onEnter: () => setOpenDd(null) },
    { selector: '[data-tour="first-bar"]', title: '일정 막대 옮기기', body: '막대를 마우스로 눌러 좌우로 드래그하면 시작일을 바로 바꿀 수 있어요.', onEnter: () => setOpenDd(null) },
    { selector: '[data-tour="menu-sigong"]', title: '시공파트너 배정', body: '파트너배정 / 알림발송 메뉴에서 공종별로 시공파트너를 배정하고 일정확인을 보낼 수 있어요.', onEnter: () => setOpenDd('dd2') },
    { selector: '[data-tour="menu-material"]', title: '자재파트너·발주일·입고일 지정', body: '자재파트너와 발주일, 입고일을 등록해두면 자재 준비 일정을 놓치지 않고 관리할 수 있어요.', onEnter: () => setOpenDd('dd2') },
    { selector: '[data-tour="add-btn"]', title: '공종 추가하기', body: '새로운 공종이나 마일스톤은 이 버튼을 눌러 추가할 수 있어요.', onEnter: () => setOpenDd(null) },
  ]

  if (!selectedSite) {
    return (
      <GanttEmptySite
        handlePDF={handlePDF}
        setOpenDd={setOpenDd}
        openDd={openDd}
        showRel={showRel}
        setShowRel={setShowRel}
        showCont={showCont}
        toggleContChip={toggleContChip}
        showVendor={showVendor}
        toggleVendorChip={toggleVendorChip}
        showMarkers={showMarkers}
        setShowMarkers={setShowMarkers}
        showFloat={showFloat}
        setShowFloat={setShowFloat}
        hideBars={hideBars}
        setHideBars={setHideBars}
        ganttPeriod={ganttPeriod}
        setGanttPeriod={setGanttPeriod}
        navigate={navigate}
        noSchedules={noSchedules}
        tourRef={tourRef}
        tourSteps={ganttTourSteps}
      />
    )
  }

  const endDt = selectedSite.endDate ? new Date(selectedSite.endDate) : null
  const dDiff = endDt ? daysBetween(today, endDt) : null
  const dDay  = dDiff===null ? '—' : dDiff>0 ? `D-${dDiff}` : dDiff===0 ? 'D-Day' : `D+${Math.abs(dDiff)}`

  // s, l을 날짜 기준으로 실시간 계산하는 헬퍼 (표시 범위 내 클리핑 포함)
  const getTaskSL = (task: GanttTask) => {
    const rawS   = Math.round((task.startDate.getTime() - ganttStart.getTime()) / 86400000)
    const rawL   = Math.max(1, Math.round((task.endDate.getTime() - task.startDate.getTime()) / 86400000) + 1)
    const s      = Math.max(rawS, 0)
    const endCol = rawS + rawL
    const l      = Math.max(0, Math.min(endCol, totalDays) - s)
    return { s, l, rawL }
  }

  // 소요일 = 작업일 수(A-09). 공휴일 조회 중·실패·범위 밖이면 null(숫자를 달력일로 대체하지 않음 — 좌측 표는 '–')
  const durationOf = (t: GanttTask) => workdaysOrNull(holidayState, holidays, t.startDate, t.endDate)

  // 마일스톤 날짜 → 마일스톤 목록 매핑 (렌더 최적화)
  const { milestoneDayMap, milestoneRowH } = buildMilestoneDayMap({ milestones, ganttStart, totalDays })

  // 동일 공종(workTypeCode)이 여러 번 등록되면 새 행이 아니라 한 행에 바를 여러 개 넣는다
  // (scheduleUtils.ts의 groupTasksIntoRows — 라이브 렌더와 PDF export가 공유).
  // [빨강·노랑만] — 신호등이 켜져 있을 때 빨강·노랑 신호가 있는 공종만 행으로 그린다
  const { displayRows, firstBarTaskId } = buildDisplayRows({ tasks, sigOn, signal, expandedRows })

  // taskId → "행" 인덱스(더 이상 tasks 배열 위치가 아니라 displayRows 배열 위치) — 접혀 있으면
  // 한 행에 바가 여러 개일 수 있어 그 바들의 taskId가 모두 같은 행 인덱스를 가리키고, 펼쳐지면
  // 바마다 자기 자신의 행 인덱스를 갖는다(대표 행에도 잠깐 다시 설정되지만 뒤이어 나오는 개별
  // 행에서 덮어써 최종적으로는 항상 실제로 바가 그려지는 행을 가리킨다).
  const taskIdxMap = new Map<number, number>()
  displayRows.forEach((row, ri) => row.bars.forEach(bar => taskIdxMap.set(bar.taskId, ri)))
  const arrows = buildArrows({ deps, tasks, taskIdxMap, getTaskSL, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, DW, rowH, barTop })

  // ── 선후관계 커넥터(위/아래 원) ──────────────────────────────
  // 실제로 바가 그려지는 태스크(taskIdxMap에 있는 것)마다 위/아래 원 좌표를 계산한다.
  // 드래그/리사이즈 중인 바는 화살표와 동일하게 그 offset을 반영해 원도 같이 따라온다.
  const connectorPoints = buildConnectorPoints({ tasks, taskIdxMap, getTaskSL, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, DW, rowH, barTop })

  // 드래그 중일 때만 계산 — 연결 가능한 후보 taskId 집합. getPredecessorCandidates는 기존
  // 선행공종 후보 로직(위치 + 순환 방지)을 그대로 재사용하고, 여기에 "이미 서로 직접 연결된
  // 사이면 다시 보여줄 필요 없다"는 조건만 추가로 걸러낸다.
  const connectCandidateIds: Set<number> | null = !connectDrag ? null : (
    connectDrag.fromEdge === 'bottom'
      ? new Set(
          tasks
            .filter(t => t.taskId !== connectDrag.fromTaskId)
            .filter(t => !deps.some(d => d.predecessorTaskId === connectDrag.fromTaskId && d.successorTaskId === t.taskId))
            .filter(t => getPredecessorCandidates(t.taskId, tasks, deps).some(c => c.taskId === connectDrag.fromTaskId))
            .map(t => t.taskId)
        )
      : new Set(
          getPredecessorCandidates(connectDrag.fromTaskId, tasks, deps)
            .filter(t => !deps.some(d => d.predecessorTaskId === t.taskId && d.successorTaskId === connectDrag.fromTaskId))
            .map(t => t.taskId)
        )
  )

  // 드래그 중이 아니면 모든 원을 보여준다. 드래그 중이면: 시작점 원(앵커) + 반대쪽 edge의
  // 유효 후보 원만 보여주고 나머지는 숨긴다 — "아래 원을 이을 땐 아래 공종들의 위 원만" 요구사항.
  const isConnectorVisible = (taskId: number, edge: 'top' | 'bottom') => {
    if (!connectDrag) return true
    if (taskId === connectDrag.fromTaskId) return edge === connectDrag.fromEdge
    const wantEdge = connectDrag.fromEdge === 'bottom' ? 'top' : 'bottom'
    return edge === wantEdge && !!connectCandidateIds?.has(taskId)
  }

  // ── 대기일(leadTime) 표시 ────────────────────────────────────
  // 대기일은 선행-후행 "관계"가 아니라 그 공정 자체가 끝난 후 반드시 대기해야 하는 기간이다
  // (예: 양생 기간) — 그래서 의존관계(deps)와 무관하게, leadTime이 있는 공정마다 자기 자신의
  // 바로 끝난 직후부터 leadTime일만큼을 자기 행에 이어서 회색으로 표시한다(후행공정 유무와
  // 무관, 후행이 없어도 대기일 자체는 그 공정의 속성이므로 그대로 보여준다).
  const floatBars = buildFloatBars({ tasks, taskIdxMap, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, ganttStart, totalDays, DW })

  const svgW = totalDays * DW
  const svgH = displayRows.length * rowH
  const hasPending = pendingChanges.size > 0

  return (
    <Page layout="fixed">

      {/* Info bar */}
      <GanttInfoBar
        selectedSite={selectedSite}
        endDt={endDt}
        dDay={dDay}
        signal={signal}
        tourRef={tourRef}
        handlePDF={handlePDF}
        setOpenDd={setOpenDd}
        openDd={openDd}
        setShowSigongPanel={setShowSigongPanel}
        openMaterialPopup={openMaterialPopup}
        setDeepLinkTaskId={setDeepLinkTaskId}
        setShowShipPanel={setShowShipPanel}
        setShowStatusPanel={setShowStatusPanel}
        setShowAddPop={setShowAddPop}
        setShowMilestonePop={setShowMilestonePop}
      />

      {/* Options bar */}
      <GanttOptionsBar
        showRel={showRel}
        setShowRel={setShowRel}
        showCont={showCont}
        toggleContChip={toggleContChip}
        showVendor={showVendor}
        toggleVendorChip={toggleVendorChip}
        showMarkers={showMarkers}
        setShowMarkers={setShowMarkers}
        showFloat={showFloat}
        setShowFloat={setShowFloat}
        sigOn={sigOn}
        hideBars={hideBars}
        setHideBars={setHideBars}
        signal={signal}
        panelOpen={selectedPanelTaskId !== null}
        ganttPeriod={ganttPeriod}
        setGanttPeriod={setGanttPeriod}
        navigate={navigate}
      />

      {/* 신호등 범례 줄 — 신호등을 켠 때만, 옵션바 바로 아래 */}
      {sigOn && (
        <SignalLegend
          title="신호등"
          caption="반장·발주·작업 순"
          items={SIGNAL_LEGEND_ITEMS}
          hint="신호등을 누르면 사유를 볼 수 있어요"
        />
      )}

      <GanttDateAlerts
        holidayStatus={holidayState.status}
        onHolidayRetry={holidayState.retry}
        orderNotice={orderNotice}
        onOrderNoticeClose={() => setOrderNotice(null)}
        markerNotice={markerNotice}
        onMarkerNoticeClose={() => setMarkerNotice(null)}
        ordersFailed={ordersStatus === 'error'}
        onOrdersRetry={retryOrders}
      />

      <GanttNoticeStrips
        dragError={dragError}
        showVendor={showVendor}
        sigOn={sigOn}
        ledgerQ={ledgerQ}
        phonesQ={phonesQ}
        hasPending={hasPending}
        pendingChanges={pendingChanges}
        handleCancel={handleCancel}
        saving={saving}
        handleSave={handleSave}
        selectedTaskIds={selectedTaskIds}
        setSelectedTaskIds={setSelectedTaskIds}
        handleDeleteSelected={handleDeleteSelected}
      />

      {/* Gantt body */}
      {loading ? (
        <div aria-busy="true" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, padding: 24 }}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} height={28} />)}</div>
      ) : loadError ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <ErrorState description="간트차트 정보를 불러올 수 없어요. 잠시 후 다시 시도해주세요." onRetry={() => setRefreshKey(k => k + 1)} />
        </div>
      ) : tasks.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState title="등록된 공종이 없습니다" description="우측 상단 버튼으로 공종을 추가하세요" />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <div
          ref={attachScrollRef}
          style={{ flex: 1, overflowX: ganttPeriod === '전체' ? 'auto' : 'hidden', overflowY: 'auto', minHeight: 0, cursor: 'grab' }}
          onMouseDown={e => { setFocusBarId(null); onContainerMouseDown(e) }}
          onMouseMove={onContainerMouseMove}
          onMouseUp={stopScrollDrag}
          onMouseLeave={stopScrollDrag}
        >
          <div style={{ width: Math.max(ganttContainerW, 212 + DW * totalDays), minWidth: '100%', background: 'var(--shell-surface)' }}>
            {/* Sticky header */}
            <GanttHeaderRows
              DAYS={DAYS}
              DW={DW}
              milestoneRowH={milestoneRowH}
              milestoneDayMap={milestoneDayMap}
              draggingMsId={draggingMsId}
              draggingMsDelta={draggingMsDelta}
              onMilestoneLabelMouseDown={onMilestoneLabelMouseDown}
            />

            {/* Task rows */}
            <div style={{ position: 'relative' }}>
              {displayRows.length === 0 && sigOn && signal.onlyRY && (
                <div role="status" style={{ padding: '18px 12px', fontSize: 12, color: 'var(--shell-on-surface-2)' }}>빨강·노랑 신호가 있는 공종이 없어요</div>
              )}
              {displayRows.map((row) => {
                const rowIsPending = row.bars.some(b => pendingChanges.has(b.taskId))
                const rowLineColor = rowIsPending ? 'color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))' : 'var(--shell-border)'
                // 대표(요약) 행은 원래 그룹의 바 전체(row.bars)를 그대로 갖고 있어 좌측 이름/메모/
                // 시공반장 표시는 가장 이른 바(row.bars[0]) 기준(개별 메모·시공반장 수정은 각 바
                // 클릭으로 열리는 사이드 패널에서 그대로 가능). 체크박스·소요일 스피너는 행의 모든
                // 바에 동시 적용 — 펼쳐진 개별 행은 bars가 그 바 하나뿐이라 자연히 그 바에만 적용된다.
                const primary = row.bars[0]
                const allSelected = row.bars.every(b => selectedTaskIds.has(b.taskId))
                const rowKey = row.isGroupHeader ? row.workTypeCode : primary.taskId

                // 드래그 중인 바가 이 행에 있으면 행 전체(라벨+타임라인)에 은은한 하이라이트를 준다 —
                // 바를 옮기는 동안 어느 공종을 옮기고 있는지 눈으로 바로 따라갈 수 있게.
                const rowIsDragging = row.bars.some(b => b.taskId === draggingTaskId)
                const rowBg = rowIsDragging
                  ? 'color-mix(in srgb, var(--gt-primary) 10%, var(--shell-surface))'
                  : rowIsPending ? 'var(--shell-bg)' : row.isExpandedChild ? 'color-mix(in srgb, var(--gt-subtle) 45%, var(--shell-surface))' : 'var(--shell-surface)'
                return (
                  <div key={rowKey} data-gantt-row-tasks={row.bars.map(b => b.taskId).join(' ')} data-gantt-header={row.isGroupHeader ? '1' : undefined} style={{ display: 'flex', position: 'relative', height: rowH, minHeight: rowH, boxSizing: 'border-box', alignItems: 'center', background: rowBg }}>
                    {/* Sticky left: name + duration */}
                    <GanttTaskRowLeft
                      rowBg={rowBg}
                      rowH={rowH}
                      rowLineColor={rowLineColor}
                      row={row}
                      allSelected={allSelected}
                      setSelectedTaskIds={setSelectedTaskIds}
                      primary={primary}
                      localRowNames={localRowNames}
                      setLocalRowNames={setLocalRowNames}
                      handleRowNameBlur={handleRowNameBlur}
                      rowIsPending={rowIsPending}
                      visibleMemoId={visibleMemoId}
                      setVisibleMemoId={setVisibleMemoId}
                      setMemoTooltipPos={setMemoTooltipPos}
                      handleRowDurationChange={handleRowDurationChange}
                      setExpandedRows={setExpandedRows}
                      durationOf={durationOf}
                      onOpenCustReq={handleOpenCustReq}
                    />

                    {/* Day grid + bars */}
                    <div style={{ flex: 1, display: 'flex', position: 'relative', height: rowH, minHeight: rowH, boxSizing: 'border-box' }}>
                      {DAYS.map((d, di) => (
                        <div key={di} style={{ width: DW, flexShrink: 0, borderRight: `var(--border-width-hairline) solid ${rowLineColor}`, height: rowH, minHeight: rowH, boxSizing: 'border-box', background: d.td ? 'var(--gt-subtle)' : (d.su || d.sa || d.holiday) ? 'var(--alpha-black-50)' : 'transparent', boxShadow: `inset 0 -1px 0 ${rowLineColor}` }} />
                      ))}
                      <div style={{ flex: 1 }} />

                      {/* 대표(요약) 행은 바 자체를 그리지 않는다 — 실제 바는 펼쳐졌을 때 이어지는
                          개별 행에서 그려진다(row.bars가 비어 있지 않은 건 체크박스·이름 표시용). */}
                      {!row.isGroupHeader && row.bars.map((task, barIdx) => {
                        const isDragging  = draggingTaskId === task.taskId
                        const isResizing  = resizingTaskId === task.taskId
                        const isPending   = pendingChanges.has(task.taskId)
                        const { s, l }    = getTaskSL(task)
                        // task.startDate는 드래그 중 불변 — offset으로 시각 위치만 조정
                        const effectiveS  = isDragging ? s + draggingOffset : s
                        const startOff    = effectiveS  // 경계 확장으로 항상 >= 0 보장
                        // 리사이즈 중에는 시작 위치는 그대로 두고 길이(소요일)만 늘거나 준다
                        const span        = isResizing ? Math.max(1, l + resizingOffset) : l
                        // 끌기 'M/D → M/D (N일)'·리사이즈 '소요일 N일 · M/D → M/D' 안내(A-12) — 놓았을 때 확정될 값(작업일·비작업일 보정 반영).
                        // 공휴일 조회 중·실패·범위 밖이면 만들지 않는다(공종명만 표시).
                        let moveTip: string | null = null
                        let resizeTip: string | null = null
                        if (isDragging && draggingOffset !== 0 && holidayGateError(holidayState,
                          task.startDate, task.endDate, addDays(task.startDate, draggingOffset), addDays(task.endDate, draggingOffset)) === null) {
                          const m = movedRange(task.startDate, task.endDate, draggingOffset, holidays)
                          moveTip = `${fmtMD(m.start)} → ${fmtMD(m.end)} (${m.days}일)`
                        }
                        if (isResizing && holidayGateError(holidayState, task.startDate, task.endDate, addDays(task.endDate, resizingOffset)) === null) {
                          const r = resizedRange(task.startDate, task.endDate, resizingOffset, holidays)
                          resizeTip = `소요일 ${r.days}일 · ${fmtMD(r.start)} → ${fmtMD(r.end)}`
                        }

                        return (
                          <Fragment key={task.taskId}>
                            <GanttBar
                              showMarkers={showMarkers && ordersStatus !== 'error'}
                              sigOn={sigOn}
                              task={task}
                              ganttStart={ganttStart}
                              DAYS={DAYS}
                              draggingMkId={draggingMkId}
                              draggingMkDelta={draggingMkDelta}
                              totalDays={totalDays}
                              shipRowsByTask={shipRowsByTask}
                              DW={DW}
                              onMkMouseDown={onMkMouseDown}
                              l={l}
                              hideBars={hideBars}
                              firstBarTaskId={firstBarTaskId}
                              onBarMouseDown={onBarMouseDown}
                              setCtxMenu={setCtxMenu}
                              barTop={barTop}
                              startOff={startOff}
                              span={span}
                              isDragging={isDragging}
                              isResizing={isResizing}
                              isPending={isPending}
                              isSelected={task.taskId === selectedPanelTaskId}
                              isFocused={task.taskId === focusBarId}
                              onBarResizeMouseDown={onBarResizeMouseDown}
                              moveTip={moveTip}
                              resizeTip={resizeTip}
                            />

                            <GanttBarLabels
                              showCont={showCont}
                              sigOn={sigOn}
                              task={task}
                              l={l}
                              startOff={startOff}
                              span={span}
                              showFloat={showFloat}
                              hideBars={hideBars}
                              row={row}
                              barIdx={barIdx}
                              getTaskSL={getTaskSL}
                              totalDays={totalDays}
                              DW={DW}
                              barTop={barTop}
                              showVendor={showVendor}
                              ledgerQ={ledgerQ}
                              phonesQ={phonesQ}
                              shipRowsByTask={shipRowsByTask}
                            />

                            <GanttSignalDots
                              sigOn={sigOn && !scheduleOnlyCodes.has(task.workTypeCode)}
                              l={l}
                              signal={signal}
                              task={task}
                              startOff={startOff}
                              span={span}
                              row={row}
                              barIdx={barIdx}
                              getTaskSL={getTaskSL}
                              totalDays={totalDays}
                              DW={DW}
                              barTop={barTop}
                              signalTaskCtx={signalTaskCtx}
                            />
                          </Fragment>
                        )
                      })}

                    </div>
                  </div>
                )
              })}

              {/* Milestone vertical lines */}
              <GanttOverlay
                milestoneDayMap={milestoneDayMap}
                draggingMsId={draggingMsId}
                draggingMsDelta={draggingMsDelta}
                DW={DW}
                svgH={svgH}
                showRel={showRel}
                sigOn={sigOn}
                hideBars={hideBars}
                chartOverlayRef={chartOverlayRef}
                svgW={svgW}
                arrows={arrows}
                selectedDepId={selectedDepId}
                setSelectedDepId={setSelectedDepId}
                connectorPoints={connectorPoints}
                isConnectorVisible={isConnectorVisible}
                connectDrag={connectDrag}
                onConnectorMouseDown={onConnectorMouseDown}
                onConnectorDrop={onConnectorDrop}
                showFloat={showFloat}
                floatBars={floatBars}
                rowH={rowH}
                barTop={barTop}
              />
            </div>
          </div>
        </div>

        {/* ── 공종 정보 사이드 패널(TaskDetailPanel) ───────────────── */}
        <GanttDetailPanelHost
          tasks={tasks}
          selectedPanelTaskId={selectedPanelTaskId}
          panelTaskRef={panelTaskRef}
          panelWorkTypeName={panelWorkTypeName}
          setPanelWorkTypeName={setPanelWorkTypeName}
          partners={partners}
          panelPartnerId={panelPartnerId}
          panelForeman={panelForeman}
          setPanelPartnerId={setPanelPartnerId}
          setPanelForeman={setPanelForeman}
          handlePartnerSent={handlePartnerSent}
          panelChangeReason={panelChangeReason}
          setPanelChangeReason={setPanelChangeReason}
          handlePartnerChanged={handlePartnerChanged}
          panelForemanSignal={panelForemanSignal}
          selectedSite={selectedSite}
          cardSup={cardSup}
          cardPt={cardPt}
          cardCr={cardCr}
          onCustReqChanged={() => setRefreshKey(k => k + 1)}
          onOrderModeChanged={() => setRefreshKey(k => k + 1)}
          openMaterialPopup={openMaterialPopup}
          handleMaterialChanged={handleMaterialChanged}
          deps={deps}
          panelPredIds={panelPredIds}
          setPanelPredIds={setPanelPredIds}
          panelMemo={panelMemo}
          setPanelMemo={setPanelMemo}
          panelNotice={panelNotice}
          panelError={panelError}
          panelSaving={panelSaving}
          commitPanel={commitPanel}
          handleDeleteTask={handleDeleteTask}
          closePanelGuarded={closePanelGuarded}
          holidayState={holidayState}
          movableRangeRef={movableRangeRef}
          applyCascadePlan={applyCascadePlan}
        />
        </div>
      )}

      {/* 하단 비고 컴포넌트 */}
      <GanttRemarkBar
        loading={loading}
        tasks={tasks}
        remarkHidden={remarkHidden}
        remarkDraft={remarkDraft}
        setRemarkHidden={setRemarkHidden}
        setRemarkDraft={setRemarkDraft}
        scheduleRemark={scheduleRemark}
        remarkSaving={remarkSaving}
        handleRemarkSave={handleRemarkSave}
      />

      {/* ── 신호 사유 팝오버 · 오늘 할일 패널 ───────────────────────────── */}
      {sigOn && signal.popover && (() => {
        const t = tasks.find(x => x.taskId === signal.popover!.taskId)
        const ts = signal.byTask.get(signal.popover.taskId)
        if (!t || !ts) return null
        return (
          <SignalPopover
            anchor={signal.popover.anchor}
            workTypeName={localRowNames[t.taskId] ?? t.workTypeName}
            task={ts}
            onClose={signal.closePopover}
            canAct={signalAction.canAct}
            onAct={item => { signal.closePopover(); signalAction.act(item) }}
            onOpenTodos={() => { const a = signal.popover!.anchor; signal.closePopover(); signal.setPanel({ anchor: a }) }}
          />
        )
      })()}
      {signal.panel && (
        <SignalTodoPanel
          anchor={signal.panel.anchor}
          data={signal.todosQ.data}
          isLoading={signal.todosQ.isLoading}
          isError={signal.todosQ.isError}
          onRetry={() => { void signal.todosQ.refetch() }}
          onClose={signal.closePanel}
          siteName={selectedSite?.siteName}
          canAct={signalAction.canAct}
          onAct={item => { signal.closePanel(); signalAction.act(item) }}
          onOpenTodoPage={schedulerHost?.openTodo ? () => { signal.closePanel(); schedulerHost.openTodo?.() } : undefined}
        />
      )}

      {/* ── 시공 팝업 ─────────────────────────────────────────────── */}
      <GanttPopupHost
        showSigongPanel={showSigongPanel}
        tasks={tasks}
        partners={partners}
        selectedSite={selectedSite}
        setShowSigongPanel={setShowSigongPanel}
        fetchPartners={fetchPartners}
        handleForemanAssigned={handleForemanAssigned}
        embedded={embedded}
        navigate={navigate}
        showMaterialPanel={showMaterialPanel}
        materialFocusTaskId={materialFocusTaskId}
        setShowMaterialPanel={setShowMaterialPanel}
        handleMaterialChanged={handleMaterialChanged}
        showShipPanel={showShipPanel}
        setShowShipPanel={setShowShipPanel}
        setDeepLinkTaskId={setDeepLinkTaskId}
        deepLinkTaskId={deepLinkTaskId}
        showStatusPanel={showStatusPanel}
        setShowStatusPanel={setShowStatusPanel}
      />

      {/* ── 메모 툴팁 (position:fixed — 마일스톤 z-index 충돌 방지) ── */}
      <GanttMemoTooltip
        visibleMemoId={visibleMemoId}
        memoTooltipPos={memoTooltipPos}
        tasks={tasks}
      />

      {/* ── 간트 바 우클릭 컨텍스트 메뉴 ──────────────────────────── */}
      <GanttContextMenu
        ctxMenu={ctxMenu}
        ctxMenuRef={ctxMenuRef}
        setAddPopDefaults={setAddPopDefaults}
        setCtxMenu={setCtxMenu}
        setShowAddPop={setShowAddPop}
      />

      {/* ── 공종 추가 팝업 ──────────────────────────────────────────── */}
      <GanttAddTaskHost
        showAddPop={showAddPop}
        selectedSite={selectedSite}
        setShowAddPop={setShowAddPop}
        setAddPopDefaults={setAddPopDefaults}
        partners={partners}
        addPopDefaults={addPopDefaults}
        insertAfterIdRef={insertAfterIdRef}
        prevTaskIdsRef={prevTaskIdsRef}
        tasksRef={tasksRef}
        setRefreshKey={setRefreshKey}
      />

      {/* ── 마일스톤 팝업 ─────────────────────────────────────────── */}
      <GanttMilestoneModal
        showMilestonePop={showMilestonePop}
        setShowMilestonePop={setShowMilestonePop}
        selectedSite={selectedSite}
        milestones={milestones}
        editingMsId={editingMsId}
        milestoneTypeOptions={milestoneTypeOptions}
        mileEditForm={mileEditForm}
        setMileEditForm={setMileEditForm}
        setEditingMsId={setEditingMsId}
        setMileFormError={setMileFormError}
        mileEditing={mileEditing}
        handleMileEditSave={handleMileEditSave}
        handleMileEditOpen={handleMileEditOpen}
        mileDeleting={mileDeleting}
        handleMileDelete={handleMileDelete}
        mileForm={mileForm}
        setMileForm={setMileForm}
        mileFormError={mileFormError}
        isMileInvalid={isMileInvalid}
        mileSaving={mileSaving}
        handleMileAdd={handleMileAdd}
      />

      {/* ── 상세창 저장 안 한 변경 이탈 확인(닫기·바깥 클릭·다른 공종 선택) ─────────── */}
      <GanttConfirmModals
        leavePrompt={leavePrompt}
        setLeavePrompt={setLeavePrompt}
        pullConfirm={pullConfirm}
        setPullConfirm={setPullConfirm}
        resolvePullConfirm={resolvePullConfirm}
      />

      {immediate.modal}
      {periodSync.modal}

      {/* ── 일정 변경 카톡 확인 창(날짜 저장 직후, 확정·회신 대기 반장 대상) ─────── */}
      <GanttChangeNoticeHost
        changeNotice={changeNotice}
        tasksRef={tasksRef}
        handleChangeNoticeSent={handleChangeNoticeSent}
        setChangeNotice={setChangeNotice}
      />

      {/* ── 화면 가이드(온보딩 투어) — [?] 클릭으로만 재생(F-05) ──────────────── */}
      <OnboardingTour ref={tourRef} storageKey="indipia_gantt_tour_seen_v1" steps={ganttTourSteps} autoStart={false} />
    </Page>
  )
}
